import { WidgetStreamRpcConfig } from 'widgets/streaming/WidgetStreamConfig';
import RequestProgressService from 'requestProgress/RequestProgressService';
import {
    WidgetStreamManager,
    WidgetStreamManagerOptions,
} from 'widgets/streaming/widgetStreamManager/WidgetStreamManager';
import { RpcRequestHandlers } from 'widgets/store/rpcStream/handlers';
import { StreamError } from 'streamsState/store/streamStatuses/StreamErrorState';
import IndigoApi from 'communication/IndigoApi';
import {
    ActionsDeferredDispatcher,
    ActionsDispatcher,
} from 'store/ActionsDispatcher';
import { using as usingStatement } from 'using-statement';

export interface WidgetRpcStreamManagerOptions<TPayload, TResponse>
    extends WidgetStreamManagerOptions<TPayload, TResponse> {
    streamConfig: WidgetStreamRpcConfig<TPayload, TResponse>;
    requestProgressService: RequestProgressService;
    indigoApi: IndigoApi;
}

/*
 * Manages a widget stream (RPC)
 */
export class WidgetRpcStreamManager<
    TPayload,
    TResponse = unknown,
> extends WidgetStreamManager<TPayload, TResponse> {
    public streamConfig: WidgetStreamRpcConfig<TPayload, TResponse>;

    private requestProgressService: RequestProgressService;
    private indigoApi: IndigoApi;
    private handlers: RpcRequestHandlers<TResponse>;

    public constructor(
        public options: WidgetRpcStreamManagerOptions<TPayload, TResponse>
    ) {
        super(options);

        const {
            widgetKey,
            loggingService,
            streamConfig,
            requestProgressService,
            indigoApi,
            store,
        } = options;

        this.streamConfig = streamConfig;
        this.requestProgressService = requestProgressService;
        this.indigoApi = indigoApi;
        this.handlers = streamConfig.buildHandlers(
            widgetKey,
            store,
            loggingService
        );

        this.log('Initialised RPC stream manager');
    }

    public invokeRequest() {
        const { clearWidgetStreamData } = this.streamConfig;

        usingStatement(
            new ActionsDeferredDispatcher(this.store),
            (dispatcher) => {
                const { indigoStreamPayload: payload } = this.buildPayload();
                if (!payload) {
                    dispatcher.dispatch(clearWidgetStreamData(this.widgetKey));
                    return;
                }
                this.getData(dispatcher, payload, 'invokeRequest');
            }
        );
    }

    protected startStreaming(
        dispatcher: ActionsDispatcher,
        payload: TPayload,
        reason: string
    ) {
        this.getData(dispatcher, payload, reason);
    }

    private getData(
        dispatcher: ActionsDispatcher,
        payload: TPayload,
        reason: string
    ) {
        this.log('Getting data', reason, payload);

        const { startChannelDestination, clearWidgetStreamData } =
            this.streamConfig;

        dispatcher.dispatch(clearWidgetStreamData(this.widgetKey));

        const apiCall = () =>
            this.indigoApi.rpcRequest<TPayload, TResponse>(
                payload,
                startChannelDestination
            );

        this.requestProgressService
            .invokeAndMonitorRequest<TResponse>(
                startChannelDestination,
                apiCall
            )
            .then((result: TResponse) => {
                this.handlers.onSuccess(result);
            })
            .catch((err: StreamError) => {
                this.handlers.onError && this.handlers.onError(err);
            });
    }

    protected stopStreaming(dispatcher: ActionsDispatcher, _reason: string) {
        const { clearWidgetStreamData } = this.streamConfig;
        dispatcher.dispatch(clearWidgetStreamData(this.widgetKey));
    }
}
